import {ErrorBoundary} from '@sentry/react';
import {
  dispatchRenderCrashEvent,
  isSentryReporting,
  type RenderCrashDetail,
} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {ReactNode} from 'react';
import {useLocation} from 'react-router-dom';

import i18n from '@dms/i18n';
import {orchestrationRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';

interface OrchestrationRouteBoundaryProps {
  children: ReactNode;
}

const UNKNOWN_CRASH_NAME = 'Error';
const UNKNOWN_CRASH_MESSAGE = 'Unknown render error';

const toRenderCrashDetail = (
  error: unknown,
  componentStack: string,
  sentryEventId: string
): RenderCrashDetail => {
  const crashContext = {
    componentStack,
    sentryEventId: isSentryReporting() ? sentryEventId : undefined,
  };

  return match(error)
    .with(Pattern.instanceOf(Error), (thrownError) => ({
      name: thrownError.name,
      message: thrownError.message,
      stack: thrownError.stack,
      ...crashContext,
    }))
    .with(Pattern.string, (message) => ({name: UNKNOWN_CRASH_NAME, message, ...crashContext}))
    .otherwise(() => ({
      name: UNKNOWN_CRASH_NAME,
      message: UNKNOWN_CRASH_MESSAGE,
      ...crashContext,
    }));
};

/**
 * Catches a render error of an orchestration page, reports it to Sentry and to the in-app bug
 * report as the app shell does, and draws the error inside the settings layout, so the rest of the
 * app stays usable. Keyed by the path, so going to another page clears the error.
 */
export function OrchestrationRouteBoundary(props: OrchestrationRouteBoundaryProps) {
  const location = useLocation();
  const listTitle = i18n.t('entity.automationRule.list.title');

  return (
    <ErrorBoundary
      key={location.pathname}
      fallback={
        <SettingsTemplate
          header={{
            title: listTitle,
            breadcrumbs: [{label: listTitle, href: orchestrationRoutes.overview}],
          }}
          isError
          data-testid={testIds.orchestration.overview('routeError')}
        />
      }
      onError={(error, componentStack, eventId) =>
        dispatchRenderCrashEvent(toRenderCrashDetail(error, componentStack, eventId))
      }
    >
      {props.children}
    </ErrorBoundary>
  );
}
